import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {EventServiceRegistrationResponse, UserSearchResult} from '../../../../../../../../../../../../@shared/models/response.module';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {EventsService} from '../../../../../../../../../../../../@shared/services/events-service';
import {ToastService} from '../../../../../../../../../../../../@core/services/toast.service';
import {_AQH_EVE_35} from '../../../../../../../../../../../../@shared/models/input.module';
import {EndpointService} from '../../../../../../../../../../../../@shared/endpoints.service';
import {debounceTime, distinctUntilChanged, switchMap, Subject} from 'rxjs';

@Component({
  selector: 'app-edit-service-registration',
  standalone: false,
  templateUrl: './edit-service-registration.html',
  styleUrl: './edit-service-registration.css'
})
export class EditServiceRegistration implements OnInit {
  @Input() registration!: EventServiceRegistrationResponse;

  form: FormGroup;
  saving = false;

  // User search
  userSearchTerm = '';
  userSearchResults: UserSearchResult[] = [];
  selectedUser: UserSearchResult | null = null;
  isSearchingUsers = false;
  private searchSubject = new Subject<string>();

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private eventsService: EventsService,
    private toastService: ToastService,
    private endpointService: EndpointService
  ) {
    this.form = this.fb.group({
      user_id: ['', Validators.required],
      num_registrations: [1, [Validators.required, Validators.min(1)]],
      notes: ['']
    });
  }

  ngOnInit() {
    if (this.registration) {
      // Set initial selected user from registration
      if (this.registration.user) {
        this.selectedUser = this.registration.user as UserSearchResult;
        this.userSearchTerm = `${this.registration.user.first_name} ${this.registration.user.last_name}`;
      }

      // Set initial form values
      this.form.patchValue({
        user_id: this.registration.user_id,
        num_registrations: this.registration.num_registrations || 1,
        notes: this.registration.notes || ''
      });
    }

    // Setup debounced user search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          return [];
        }
        this.isSearchingUsers = true;
        return this.endpointService.aqh_eve_67({ search: query });
      })
    ).subscribe({
      next: (response: any) => {
        this.isSearchingUsers = false;
        this.userSearchResults = response?.data || [];
      },
      error: () => {
        this.isSearchingUsers = false;
        this.userSearchResults = [];
      }
    });
  }

  onUserSearch() {
    this.searchSubject.next(this.userSearchTerm);
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.form.patchValue({ user_id: user.user_id });
    this.userSearchTerm = user.full_name || `${user.first_name} ${user.last_name}`;
    this.userSearchResults = [];
  }

  clearUserSearch() {
    this.userSearchTerm = '';
    this.selectedUser = null;
    this.form.patchValue({ user_id: '' });
    this.userSearchResults = [];
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload: _AQH_EVE_35 = {
      event_service_registration_id: this.registration.event_service_registration_id,
      user_id: this.form.get('user_id')!.value,
      num_registrations: this.form.get('num_registrations')!.value,
      notes: this.form.get('notes')!.value || ''
    };

    this.eventsService.updateEventServiceRegistration(payload).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success('Registration updated successfully');
        this.activeModal.close(true);
      },
      error: () => {
        this.saving = false;
        this.toastService.error('Failed to update registration');
      }
    });
  }

  close() {
    this.activeModal.close();
  }

  get userIdInvalid(): boolean {
    const c = this.form.get('user_id');
    return !!(c && c.invalid && c.touched);
  }
}
